/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * BooleanIndicator - Indicador de status on/off ou alarme
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Circle } from 'lucide-react';

const BooleanIndicator = ({
  value = false,
  label = '',
  showLabel = true,
  size = 'medium', // 'small', 'medium', 'large'
  variant = 'default', // 'default', 'alarm', 'status'
  trueLabel = 'Ativo',
  falseLabel = 'Inativo',
  trueColor = '#22c55e',
  falseColor = '#6b7280',
}) => {
  const isActive = Boolean(value);

  // Tamanhos
  const sizes = {
    small: {
      container: 'w-20 h-16',
      icon: 16,
      text: 'text-xs',
      label: 'text-xs',
      padding: 'p-2',
    },
    medium: {
      container: 'w-28 h-20',
      icon: 24,
      text: 'text-sm',
      label: 'text-xs',
      padding: 'p-3',
    },
    large: {
      container: 'w-36 h-24',
      icon: 32,
      text: 'text-base',
      label: 'text-sm',
      padding: 'p-4',
    },
  };

  const currentSize = sizes[size] || sizes.medium;
  const currentColor = isActive ? trueColor : falseColor;

  // Ícones baseados no variant
  const getIcon = () => {
    const iconProps = {
      size: currentSize.icon,
      color: currentColor,
      className: isActive ? 'drop-shadow-lg' : '',
      style: isActive ? { filter: `drop-shadow(0 0 8px ${currentColor})` } : {},
    };

    switch (variant) {
      case 'alarm':
        return isActive 
          ? <AlertTriangle {...iconProps} className="animate-pulse" />
          : <Circle {...iconProps} />;
      case 'status':
        return isActive 
          ? <CheckCircle2 {...iconProps} />
          : <XCircle {...iconProps} />;
      default:
        return (
          <div 
            className={`rounded-full transition-all duration-300 ${isActive ? 'animate-pulse' : ''}`}
            style={{
              width: currentSize.icon,
              height: currentSize.icon,
              backgroundColor: currentColor,
              boxShadow: isActive ? `0 0 20px ${currentColor}` : 'none',
            }}
          />
        );
    }
  };

  return (
    <div 
      className={`
        ${currentSize.container} ${currentSize.padding}
        flex flex-col items-center justify-center gap-1
        bg-gray-900 rounded-lg border border-gray-700
        relative overflow-hidden transition-all duration-300
      `}
      style={{
        borderColor: isActive ? currentColor + '40' : undefined,
      }}
    >
      {/* Efeito de glow no fundo quando ativo */}
      {isActive && (
        <div 
          className="absolute inset-0 opacity-15 animate-pulse"
          style={{ 
            background: `radial-gradient(circle at center, ${currentColor} 0%, transparent 70%)`,
          }}
        />
      )}

      {/* Label */}
      {showLabel && label && (
        <div className={`text-gray-400 ${currentSize.label} font-medium truncate w-full text-center`}>
          {label}
        </div>
      )}

      {/* Ícone indicador */}
      <div className="relative z-10">
        {getIcon()}
      </div>

      {/* Status text */}
      <span 
        className={`${currentSize.text} font-medium`}
        style={{ color: currentColor }}
      >
        {isActive ? trueLabel : falseLabel}
      </span>
    </div>
  );
};

export default BooleanIndicator;
